import React from 'react';
import {graphql} from 'react-relay';
// NAMED export. ForecastQueryRenderer.js:16 is `export const ForecastQueryRenderer`
// and has no default; a default import resolves to undefined and React throws
// "Element type is invalid" the first time the drawer opens.
import {ForecastQueryRenderer} from '../../../ForecastQueryRenderer';
import {ManageTaskSetsDrawer} from '../../../forecast-app/project-tab/projects/scoping-page/task-set/ManageTaskSetsDrawer';
import {EVENT_ID, dispatch} from '../../../containers/event_manager';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import ProjectUtil from '../../../forecast-app/shared/util/ProjectUtil';
import Util from '../../../forecast-app/shared/util/util';
import {PERMISSION_TYPE} from '../../../Permissions';

// `project(id: $projectId)` with a String argument -- the convention this page
// already uses at canvas_scheduling.js:496.
const query = graphql`
	query TimelineManageTaskSetsDrawer_Query($projectId: String) {
		viewer {
			actualPersonId
			component(name: "timeline_manage_task_sets")
			company {
				currency
				roles(first: 1000000) {
					edges {
						node {
							id
							name
						}
					}
				}
				labels(first: 1000000, labelType: TASK) {
					edges {
						node {
							id
							name
							color
							category {
								id
								name
							}
						}
					}
				}
			}
			project(id: $projectId) {
				# The GLOBAL id, which is what the edit form and the delete mutation
				# take -- not the route's custom id in $projectId above.
				id
				estimationUnit
				budgetType
				rateCard {
					currency
				}
				phases(first: 1000000) {
					edges {
						node {
							id
							name
						}
					}
				}
				projectPersons(first: 1000000) {
					edges {
						node {
							person {
								id
								firstName
								lastName
							}
						}
					}
				}
				...ManageTaskSetsDrawer_project
			}
		}
	}
`;

/** A Relay connection as this query selects them. */
interface Edges<T> {
	readonly edges?: ReadonlyArray<{readonly node?: T | null} | null> | null;
}

/** Only the parts of the query result this component reads. */
interface TimelineDrawerViewer {
	readonly company?: {
		readonly currency?: string | null;
		readonly roles?: Edges<{id: string; name?: string | null}> | null;
		readonly labels?: Edges<{
			id: string;
			name?: string | null;
			color?: string | null;
			category?: {id: string; name?: string | null} | null;
		}> | null;
	} | null;
	readonly project?: {
		readonly id?: string;
		readonly estimationUnit?: string | null;
		readonly budgetType?: string | null;
		readonly rateCard?: {readonly currency?: string | null} | null;
		readonly phases?: Edges<{id: string; name?: string | null}> | null;
		readonly projectPersons?: Edges<{
			person?: {id: string; firstName?: string | null; lastName?: string | null} | null;
		}> | null;
	} | null;
}

/** The nodes of a connection, with the nulls Relay types allow dropped. */
const edgeNodes = <T,>(connection?: Edges<T> | null): T[] =>
	(connection?.edges ?? []).map(edge => edge?.node).filter((node): node is T => !!node);

export interface TimelineManageTaskSetsDrawerProps {
	/** The project's custom or company id. Null on a project group timeline. */
	projectId: string | null;
	isOpen: boolean;
	highlightSetId?: string | null;
	/** Passed straight through; see ManageTaskSetsDrawer's openNonce. */
	openNonce?: number;
	onClose: () => void;
}

/**
 * The manage drawer on the project timeline.
 *
 * The set list is fetched when the surface opens rather than on the page query:
 * the timeline's query is already the heaviest in the app, and the pattern
 * fields and stored defaults across every set on a project are payload nobody
 * reads until this drawer is on screen. The handful of scalars the CHIP needs
 * are a different matter and do ride on the page query -- see
 * canvasScheduling_viewer.
 *
 * Renders nothing without a project: the group timeline route resolves none.
 *
 * PIKA-773: this also carried an `isPsProject` refusal for AC4's PS half, fed from
 * `!!viewer.psProject`. That is not a PS-only signal (ViewerType.js:797 resolves it
 * from the project service for ordinary projects too), so the drawer refused to
 * render on every project timeline. Removed rather than re-guessed -- see the note
 * in projects_scheduling_header.js.
 *
 * Both refreshes after a change are owned here rather than by the canvas that
 * mounts this, because both are answers to the same timeline-only problem: the
 * canvas draws from its own data, not the Relay store, so nothing it shows
 * moves on its own. The scoping page needs neither -- it refetches its page
 * query and the drawer reads a fragment off it.
 */
/**
 * Brings the timeline back in line with a set that just changed.
 *
 * Two things are stale, and they need different treatment. The drawer's own
 * query holds the set list, so it is re-run -- otherwise a deleted set stays in
 * the list and an edited one keeps its old instance count. The canvas holds the
 * task bars and everything derived from them (hours, revenue, committed cost,
 * heatmap demand), and it builds those from its own fetch rather than the Relay
 * store, so it is told to reload.
 *
 * A reload, not an incremental update: create can patch the canvas in place
 * because its mutation returns the new instances, but the update payload
 * carries no tasks at all and a set delete removes many at once. The cost is
 * the reader's expanded groups and scroll position; the alternative is totals
 * that sit still beside the change that should have moved them, in a drawer
 * deliberately built to keep the table visible next to it.
 */
const refresh = (retry?: () => void) => {
	retry?.();
	dispatch(EVENT_ID.SCHEDULING_RELOAD_DATA);
};

export const TimelineManageTaskSetsDrawer = ({
	projectId,
	isOpen,
	highlightSetId = null,
	openNonce = 0,
	onClose,
}: TimelineManageTaskSetsDrawerProps) => {
	const [hasOpened, setHasOpened] = React.useState(false);

	React.useEffect(() => {
		if (isOpen) {
			setHasOpened(true);
		}
	}, [isOpen]);

	// No project, no set list -- the group timeline route resolves none.
	if (!projectId || !hasOpened) {
		// `hasOpened` keeps the drawer mounted after the first open so TaskSetDrawer
		// can run its slide-out transition -- returning null on !isOpen would make
		// the drawer vanish instead of closing. The query still does not run until
		// the first open, which is the point of fetching here rather than on the
		// page query.
		return null;
	}

	return (
		<ForecastQueryRenderer
			key="query-render-timeline-manage-task-sets"
			query={query}
			variables={{projectId}}
			customLoader={() => null}
			// ForecastQueryRenderer.js:169 calls props.render(relayProps, retry) with
			// the QUERY ROOT, so the project hangs off relayProps.viewer -- reading
			// relayProps.project would be undefined and the list would render empty
			// with no error.
			render={(relayProps: {viewer?: TimelineDrawerViewer}, retry?: () => void) => {
				const viewer = relayProps?.viewer;
				const project = viewer?.project;
				return (
					<ManageTaskSetsDrawer
						isOpen={isOpen}
						project={project}
						highlightSetId={highlightSetId}
						openNonce={openNonce}
						/* Everything below is what the config pane's edit form needs.
						   Without it the form has no project to save against and does
						   not render at all, so a set opened from the timeline showed
						   an empty drawer, and delete returned early and did nothing.

						   The GLOBAL id, not the `projectId` prop above: that one is
						   the route's custom id ("P10"), which the query takes, while
						   the form and the delete mutation take the Relay id. */
						projectId={project?.id}
						isPointsProject={project?.estimationUnit === 'POINTS'}
						roles={edgeNodes(viewer?.company?.roles)}
						assignablePersons={edgeNodes(project?.projectPersons)
							.map(projectPerson => projectPerson.person)
							.filter((person): person is NonNullable<typeof person> => !!person)}
						phases={edgeNodes(project?.phases)}
						labels={edgeNodes(viewer?.company?.labels)}
						showBillable={ProjectUtil.projectTracksRevenue(project ?? null)}
						currencySymbol={Util.GetCurrencySymbol(
							project?.rateCard?.currency ?? viewer?.company?.currency ?? null
						)}
						/* The same two permissions the scoping page reads. Without
						   them the drawer treats every viewer as unable to act. */
						canEditTasks={hasPermission(PERMISSION_TYPE.TASK_UPDATE)}
						canDeleteTasks={hasPermission(PERMISSION_TYPE.TASK_DELETE)}
						onClose={onClose}
						onDeleted={() => refresh(retry)}
						onEdited={() => refresh(retry)}
					/>
				);
			}}
		/>
	);
};
